import React, { useEffect, useState } from "react";
import { Loader2, Check, Sparkles, Copy, Gift, Rocket, CreditCard } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BillingCycleToggle } from "@/pages/Pricing";
import { StripeBadge } from "@/components/StripeBadge";
import { DeviceSessions } from "@/components/DeviceSessions";
import { toast } from "sonner";

export default function Billing() {
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [refs, setRefs] = useState(null);
  const [busy, setBusy] = useState("");
  const [cycle, setCycle] = useState("monthly");

  const load = () => {
    api.get("/billing/me").then((r) => {
      setMe(r.data);
      api.get("/billing/plans", { params: { account_type: r.data.account_type } })
        .then((p) => setPlans(p.data)).catch(() => {});
    }).catch(() => {});
    api.get("/referrals").then((r) => setRefs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open the billing portal");
      setBusy("");
    }
  };

  const startTrial = async (plan) => {    setBusy(`trial-${plan}`);
    try {
      await api.post("/billing/trial", { plan });
      toast.success(`Your ${plans.trial_days}-day free trial has started`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  if (!plans || !me) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const link = `${window.location.origin}/login?ref=${refs?.referral_code || me.referral_code}`;
  const pct = me.unlimited ? 100 : Math.min(100, Math.round((me.used / Math.max(1, me.quota + me.bonus)) * 100));

  return (
    <div className="max-w-5xl">
      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Billing</p>
      <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Plan & usage</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-8">
        {me.account_type === "contractor"
          ? "Your monthly allowance covers RFQs and their comparisons. Vendor search and editing are always unlimited."
          : "Automatic quotes are BOQ uploads matched by AI. Editing, revising and exporting is always unlimited."}
      </p>

      <div data-testid="usage-card" className="bg-card border border-border rounded-2xl p-6 mb-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-sm text-muted-foreground">Current plan</div>
            <div data-testid="current-plan" className="font-display text-2xl font-extrabold text-slate-900 dark:text-slate-100">
              {me.plan_name}
              {me.plan_expires_at && (
                <span className="ml-2 text-xs font-semibold text-brand align-middle">
                  {me.cancel_at_period_end ? "ends" : "until"} {new Date(me.plan_expires_at).toLocaleDateString()}
                </span>
              )}
            </div>
            {me.has_subscription && (
              <Button variant="outline" size="sm" data-testid="manage-subscription-button"
                      onClick={portal} disabled={busy === "portal"}
                      className="mt-3 h-9 gap-2 font-semibold">
                {busy === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Manage subscription
              </Button>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">This month</div>
            <div data-testid="usage-count" className="font-mono font-bold text-lg">
              {me.used} / {me.unlimited ? "∞" : me.quota + me.bonus}
            </div>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
          <div className={`h-full rounded-full ${pct >= 100 && !me.unlimited ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
        {me.cancel_at_period_end && (
          <p data-testid="cancel-notice" className="text-xs text-amber-800 dark:text-amber-200 bg-amber-50/70 dark:bg-amber-950/20 border border-amber-300/70 rounded-xl px-4 py-3 mt-3">
            Your subscription is set to cancel. You keep every {me.plan_name} feature until{" "}
            {me.plan_expires_at ? new Date(me.plan_expires_at).toLocaleDateString() : "the end of the paid period"},
            then the account moves to Free. Change your mind any time from Manage subscription.
          </p>
        )}
        {!me.unlimited && me.remaining === 0 && (          <p data-testid="quota-warning" className="text-xs text-destructive font-semibold mt-3">
            {me.account_type === "contractor"
              ? "You have used every RFQ on this plan for the month. Upgrade to Pro for unlimited RFQs and comparisons."
              : "You have used every automatic quote this month. BOQ uploads still work — they just come in unpriced for manual rating. Upgrade to keep AI matching."}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">Plans</div>
          <p data-testid="billing-tagline" className="text-sm text-muted-foreground mt-0.5">
            Premium features for free. All features for less.
          </p>
        </div>
        <BillingCycleToggle cycle={cycle} setCycle={setCycle} discount={plans.yearly_discount || 20} />
      </div>

      <div className={`grid grid-cols-1 gap-4 ${plans.plans.length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {plans.plans.map((p) => {
          const current = me.plan === p.id;
          const yearly = cycle === "yearly";
          const amount = yearly ? p.price_yearly : p.price;
          const key = yearly ? p.lookup_key_yearly : p.lookup_key;
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={`rounded-2xl border p-6 flex flex-col ${["pro", "gold"].includes(p.id) ? "border-brand/50 bg-brand-soft/40" : "border-border bg-card"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display text-lg font-extrabold">{p.name}</div>
                {["pro", "gold"].includes(p.id) && (
                  <span className="px-2 py-0.5 rounded-full bg-brand text-brand-foreground text-[10px] font-bold uppercase tracking-wide">
                    Most complete
                  </span>
                )}
              </div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-3xl font-extrabold">{plans.currency} {amount}</span>
                <span className="text-xs text-muted-foreground mb-1">/{yearly ? "year" : "month"}</span>
              </div>
              {amount > 0 && yearly && (
                <div className="text-xs font-semibold text-brand mt-1">Saves {plans.yearly_discount || 20}% versus monthly</div>
              )}
              <ul className="mt-5 space-y-2 flex-1">
                {p.perks.map((k) => (
                  <li key={k} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {k}
                  </li>
                ))}
              </ul>
              <div className="mt-6 space-y-2">
                {current ? (
                  <Button data-testid={`plan-current-${p.id}`} disabled variant="outline" className="w-full font-semibold">Current plan</Button>
                ) : key ? (
                  <>
                    <Button
                      data-testid={`plan-select-${p.id}`}
                      onClick={() => checkout(key)}
                      disabled={busy === key}
                      className={`w-full font-semibold gap-2 ${["pro", "gold"].includes(p.id) ? "bg-brand text-brand-foreground hover:bg-brand/90" : ""}`}
                    >
                      {busy === key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Choose {p.name}
                    </Button>
                    {me.trial_available && (plans.trial_plans || []).includes(p.id) && (
                      <Button
                        data-testid={`plan-trial-${p.id}`}
                        variant="outline"
                        onClick={() => startTrial(p.id)}
                        disabled={busy === `trial-${p.id}`}
                        className="w-full font-semibold gap-2"
                      >
                        {busy === `trial-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                        Start {plans.trial_days}-day free trial
                      </Button>
                    )}
                  </>
                ) : (
                  <Button disabled variant="ghost" className="w-full">Included</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mt-6">
        <StripeBadge testid="billing-stripe-badge" />
        <span className="text-xs font-medium text-muted-foreground">Cancel anytime · no hidden fees</span>
      </div>

      <div data-testid="referral-card" className="bg-card border border-border rounded-2xl p-6 mt-8">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-brand" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-900 dark:text-slate-100">Refer another vendor</div>
            <p className="text-sm text-muted-foreground mt-1">{refs?.reward}</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <code data-testid="referral-link" className="flex-1 text-xs bg-secondary rounded-lg px-3 py-2.5 font-mono truncate">{link}</code>
              <Button
                data-testid="copy-referral-button"
                variant="outline"
                onClick={() => { navigator.clipboard?.writeText(link); toast.success("Referral link copied"); }}
                className="gap-2 font-semibold"
              >
                <Copy className="w-4 h-4" /> Copy link
              </Button>
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              Code <span className="font-mono font-bold text-foreground">{refs?.referral_code || me.referral_code}</span>
              {" · "}{refs?.summary?.total ?? 0} joined · {refs?.summary?.rewarded ?? 0} rewarded
            </div>
            {refs?.referrals?.length > 0 && (
              <div className="mt-4 divide-y divide-border">
                {refs.referrals.map((x, i) => (
                  <div key={i} data-testid={`referral-row-${i}`} className="py-2 flex items-center justify-between text-xs">
                    <span className="font-medium">{x.company_name}</span>
                    <span className={`px-2 py-0.5 rounded-md font-bold ${x.status === "rewarded" ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                      {x.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-6">
        <DeviceSessions />
      </div>
    </div>
  );
}
